import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import DataCacheService from '@/shared/services/DataCacheService';
import userStorage from '@/shared/services/userStorage';
import messages from './registrationMessages';

const STORAGE_KEY = 'distanceDialog';

const getAddressString = address =>
  [
    address.street1,
    ' ',
    address.number,
    address.box ? `/${address.box}` : '',
    ', ',
    address.zipCode,
    ' ',
    address.city,
    ', ',
    address.country,
  ].join('');

/**
 * Calculates the driving distance between an origin (own company / employee address) and a
 * relation address using the Google Maps distance matrix, mirroring the legacy
 * `CalcDistanceDialogCtrl`. On save it returns `{ distance, description }` for the travel line.
 */
class CalcDistanceModal extends Component {
  constructor(props) {
    super(props);
    const employee = DataCacheService.getDataCache()?.employee || {};
    const settings = userStorage.getItem(STORAGE_KEY) || { addRoute: true };
    this.state = {
      from: { value: '', list: [] },
      to: { value: '', list: [] },
      singleTravel: employee.travelOneWay ? 1 : 2,
      addRoute: settings.addRoute !== false,
      calculating: false,
      result: null,
      noRelAddress: false,
      noCompAddress: false,
    };
  }

  componentDidMount() {
    const { relationId, companyId } = this.props;
    const cache = DataCacheService.getDataCache() || {};
    const employee = cache.employee || {};

    Promise.all([
      api.base.get(`${config.appUrls.api}/api/company/addresses`),
      api.base.get(`${config.appUrls.api}/api/address/${relationId}`),
    ]).then(([{ data: companyAddresses }, { data: relationAddresses }]) => {
      const from = { value: '', list: [] };
      const to = { value: '', list: [] };

      (companyAddresses || [])
        .filter(x => x.street1)
        .forEach(companyAddress => {
          const address = getAddressString(companyAddress);
          if (from.list.indexOf(address) < 0) from.list.push(address);
          if (companyId && companyId === companyAddress.companyId) from.value = address;
          else if (!companyId && cache.tenantId === companyAddress.companyId) from.value = address;
        });

      if (employee.employeeAddress && employee.employeeAddress.street1) {
        const emplAddr = getAddressString(employee.employeeAddress);
        from.list.push(emplAddr);
        if (employee.travelFromHome || !from.value) from.value = emplAddr;
      }

      const noCompAddress = from.list.length === 0;
      if (!noCompAddress && !from.value) [from.value] = from.list;

      (relationAddresses || []).forEach(({ medium, postAddress, isMain = false }) => {
        if (medium !== 0) return;
        if (!postAddress || !postAddress.street1) return;
        const addr = getAddressString(postAddress);
        to.list.push(addr);
        if (isMain) to.value = addr;
      });

      const noRelAddress = to.list.length === 0;
      if (!noRelAddress && !to.value) [to.value] = to.list;

      this.setState({ from, to, noCompAddress, noRelAddress }, () => {
        if (this.canCalculate()) this.calculate();
      });
    });
  }

  canCalculate = () => {
    const { calculating, from, to } = this.state;
    return !calculating && !!from.value && !!to.value;
  };

  calculate = () => {
    if (!this.canCalculate()) return;
    const { from, to } = this.state;
    const maps = typeof window !== 'undefined' ? window.google?.maps : null;
    if (!maps) {
      const { intl } = this.props;
      this.setState({ result: { error: 'Google Maps is not available' } });
      ToastrService.error(intl.formatMessage(messages.Error));
      return;
    }
    this.setState({ calculating: true, result: null });
    const service = new maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [from.value],
        destinations: [to.value],
        travelMode: maps.TravelMode.DRIVING,
        unitSystem: maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status !== 'OK' || !response?.rows?.length) {
          this.setState({ calculating: false, result: { error: status || 'ERROR' } });
          return;
        }
        const element = response.rows[0].elements[0];
        if (!element || element.status !== 'OK') {
          this.setState({ calculating: false, result: { error: element?.status || 'ERROR' } });
          return;
        }
        this.setState({
          calculating: false,
          result: {
            origin: response.originAddresses[0],
            destination: response.destinationAddresses[0],
            distance: element.distance.value / 1000,
            durationMinutes: Math.round(element.duration.value / 60),
          },
        });
      },
    );
  };

  flip = () => this.setState(({ from, to }) => ({ from: to, to: from, result: null }));

  updateSettings = patch => {
    this.setState(patch, () => {
      const { addRoute } = this.state;
      userStorage.setItem(STORAGE_KEY, { addRoute });
    });
  };

  getDescription = () => {
    const { addRoute, result, singleTravel } = this.state;
    const { intl } = this.props;
    if (!addRoute || !result || result.error) return null;
    let description = `${intl.formatMessage(messages.Origin)}: ${result.origin}\n`;
    description += `${intl.formatMessage(messages.Destination)}: ${result.destination}\n`;
    if (singleTravel === 2)
      description += `${intl.formatMessage(messages.AndBack)}: ${result.origin}\n`;
    description += `${intl.formatMessage(messages.Distance)}: ${(
      result.distance * singleTravel
    ).toFixed(2)} km`;
    return description;
  };

  save = () => {
    const { result, singleTravel } = this.state;
    const { onSave } = this.props;
    if (!result || result.error) return;
    onSave({
      distance: Number((result.distance * singleTravel).toFixed(2)),
      description: this.getDescription(),
    });
  };

  renderAddressSelect = (which, value, list) => (
    <select
      className="form-control input-sm"
      value={value}
      onChange={e => this.setState({ [which]: { value: e.target.value, list }, result: null })}
    >
      {list.map(address => (
        <option key={address} value={address}>
          {address}
        </option>
      ))}
    </select>
  );

  render() {
    const { intl, show, onClose } = this.props;
    const { from, to, singleTravel, addRoute, result, noRelAddress, noCompAddress } = this.state;

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.CalculateDistance)}
        show={show}
        onClose={onClose}
        onSave={this.save}
        disabled={!result || !!result.error}
        leftButton={
          <span className="bold">
            {noRelAddress && <div>{intl.formatMessage(messages.NoAddressForRelation)}</div>}
            {noCompAddress && <div>{intl.formatMessage(messages.NoAddressInCompany)}</div>}
          </span>
        }
      >
        <form className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-md-3">{intl.formatMessage(messages.Origin)}</label>
            <div className="col-md-9">
              {this.renderAddressSelect('from', from.value, from.list)}
            </div>
          </div>
          <div className="form-group">
            <div className="col-md-12 text-center">
              <button type="button" className="btn btn-default" onClick={this.flip}>
                <i className="fa fa-exchange fa-rotate-90" />
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-md-3">
              {intl.formatMessage(messages.Destination)}
            </label>
            <div className="col-md-9">{this.renderAddressSelect('to', to.value, to.list)}</div>
          </div>
          <div className="form-group">
            <div className="col-md-6 col-md-offset-3">
              <label className="radio-inline">
                <input
                  type="radio"
                  name="singleTravel"
                  checked={singleTravel === 1}
                  onChange={() => this.updateSettings({ singleTravel: 1 })}
                />
                {intl.formatMessage(messages.OneWay)}
              </label>
              <label className="radio-inline">
                <input
                  type="radio"
                  name="singleTravel"
                  checked={singleTravel === 2}
                  onChange={() => this.updateSettings({ singleTravel: 2 })}
                />
                {intl.formatMessage(messages.AndBack)}
              </label>
            </div>
            <div className="col-md-3">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={addRoute}
                  onChange={e => this.updateSettings({ addRoute: e.target.checked })}
                />
                {intl.formatMessage(messages.AddRouteToDescription)}
              </label>
            </div>
          </div>
          <div className="form-group">
            <div className="col-md-9 col-md-offset-3">
              <button
                type="button"
                className="btn btn-inverse"
                disabled={!this.canCalculate()}
                onClick={this.calculate}
              >
                <i className="fa fa-calculator margin5R" />
                {intl.formatMessage(messages.Calculate)}
              </button>
            </div>
          </div>

          {result && result.error && (
            <div className="form-group">
              <label className="col-md-3 control-label">{intl.formatMessage(messages.Error)}</label>
              <div className="col-md-9 form-control-static">{result.error}</div>
            </div>
          )}
          {result && !result.error && (
            <div className="form-group">
              <label className="col-md-3 control-label">
                {intl.formatMessage(messages.Distance)}
              </label>
              <div className="col-md-9 form-control-static">
                {(result.distance * singleTravel).toFixed(2)} km
              </div>
            </div>
          )}
          {result && !result.error && addRoute && (
            <div className="form-group">
              <label className="col-md-3 control-label">
                {intl.formatMessage(messages.Description)}
              </label>
              <div className="col-md-9">
                <pre>{this.getDescription()}</pre>
              </div>
            </div>
          )}
        </form>
      </OkCancelModal>
    );
  }
}

CalcDistanceModal.propTypes = {
  relationId: PropTypes.string.isRequired,
  companyId: PropTypes.string,
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

CalcDistanceModal.defaultProps = {
  companyId: null,
  show: false,
};

export default injectIntl(CalcDistanceModal);
